import React, { useCallback, useMemo, useRef } from 'react';
import {
  Box,
  Typography,
  Grid,
  Chip,
  Paper,
  Divider,
  TextField,
  Autocomplete,
} from '@mui/material';
import LayersIcon from '@mui/icons-material/Layers';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import { environmentSafeLog } from '../../utils/environment';
import { CompositesLayerMetadata } from '../../types/referencelayerMetadata';
import { validateCompositeMetadata, CompositeValidationResult } from '../../utils/metadataValidation';

interface CompositeMetadataEditorProps {
  layerMetadata: CompositesLayerMetadata;
  isEditable?: boolean;
  onMetadataChange?: (updatedMetadata: CompositesLayerMetadata) => void;
  // Legacy support during migration
  asset?: any;
  // Component assets for validation
  componentAssets?: {
    songs?: any;
    stars?: any;
    looks?: any;
    moves?: any;
    worlds?: any;
  };
}

const CompositeMetadataEditor: React.FC<CompositeMetadataEditorProps> = ({
  layerMetadata,
  asset,
  isEditable = false,
  onMetadataChange,
  componentAssets = {},
}) => {
  // V2.0 ARCHITECTURE: Use layerMetadata first, with legacy fallback
  const compositeMetadata = React.useMemo(() => {
    // Priority 1: V2.0 layerMetadata prop (preferred)
    if (layerMetadata) {
      environmentSafeLog('📌 [COMPOSITE V2.0] Using layerMetadata prop');
      return layerMetadata;
    }

    // Legacy fallback for backward compatibility
    console.log(
      '🚨 [COMPOSITE AI CONTRACT] Full asset object received:',
      asset
    );

    environmentSafeLog(
      '🔎 [COMPOSITE AI CONTRACT] Extracting compositeMetadata from asset:',
      {
        hasCompositeMetadata: !!asset.compositeMetadata,
        hasAiContractMetadata: !!asset.aiMetadata?.layerMetadata,
        hasAiCompositeMetadata: !!asset.aiMetadata?.compositeMetadata,
        hasMetadataCompositeMetadata: !!asset.metadata?.compositeMetadata,
      }
    );

    let rawMetadata = null;

    // Priority 1: AI Contract layerMetadata (from AI Response Contract)
    if (asset.aiMetadata?.layerMetadata && asset.layer === 'C') {
      environmentSafeLog(
        '📌 [COMPOSITE AI CONTRACT] Using aiMetadata.layerMetadata from AI Response Contract'
      );
      rawMetadata = asset.aiMetadata.layerMetadata;
    }
    // Priority 2: Root level compositeMetadata (legacy AI-generated)
    else if (asset.compositeMetadata) {
      environmentSafeLog(
        '📌 [COMPOSITE AI CONTRACT] Using root compositeMetadata (legacy)'
      );
      rawMetadata = asset.compositeMetadata;
    }
    // Priority 3: AI metadata structure (nested legacy)
    else if (asset.aiMetadata?.compositeMetadata) {
      environmentSafeLog(
        '📌 [COMPOSITE AI CONTRACT] Using aiMetadata.compositeMetadata (legacy)'
      );
      rawMetadata = asset.aiMetadata.compositeMetadata;
    }
    // Priority 4: Legacy metadata structure
    else if (asset.metadata?.compositeMetadata) {
      environmentSafeLog(
        '📌 [COMPOSITE AI CONTRACT] Using metadata.compositeMetadata (legacy)'
      );
      rawMetadata = asset.metadata.compositeMetadata;
    } else {
      environmentSafeLog(
        '⚠️ [COMPOSITE AI CONTRACT] No composite metadata found, using empty object'
      );
      rawMetadata = {};
    }

    // Apply nested data access pattern for backward compatibility
    const actualCompositeMetadata =
      rawMetadata?.compositeMetadata ||
      rawMetadata?.data?.compositeMetadata ||
      rawMetadata;
    environmentSafeLog(
      '🔄 [COMPOSITE AI CONTRACT] Final metadata after nested access:',
      actualCompositeMetadata
    );

    return actualCompositeMetadata;
  }, [asset]);

  // Composite Validation
  const validationResult = useMemo<CompositeValidationResult>(() => {
    return validateCompositeMetadata(compositeMetadata, componentAssets);
  }, [compositeMetadata, componentAssets]);

  // State for editable fields - initialize with extracted metadata
  const [editableData, setEditableData] = React.useState(() => {
    console.log(
      '🚀 [COMPOSITE INFO INIT] Initializing editableData state with compositeMetadata:',
      compositeMetadata
    );
    return {
      components: compositeMetadata?.components || {
        song: '',
        star: '',
        look: '',
        move: '',
        world: '',
      },
      totalComponents: compositeMetadata?.totalComponents || 0,
      primaryLayer: compositeMetadata?.primaryLayer || '',
      synergy: compositeMetadata?.synergy || '',
      complexity: compositeMetadata?.complexity || '',
      productionValue: compositeMetadata?.productionValue || '',
      targetAudience: compositeMetadata?.targetAudience || '',
      assetType: compositeMetadata?.assetType || 'base',
      baseCompositeId: compositeMetadata?.baseCompositeId || '',
      variantName: compositeMetadata?.variantName || '',
      variantType: compositeMetadata?.variantType || '',
    };
  });

  // Track metadata hash to prevent infinite update loops
  const metadataHashRef = useRef<string>('');
  const computedHash = JSON.stringify(compositeMetadata);

  // Update editableData when compositeMetadata changes (from AI generation)
  React.useEffect(() => {
    if (computedHash !== metadataHashRef.current) {
      console.log(
        '🔄 [COMPOSITE INFO] compositeMetadata changed, updating editableData'
      );
      metadataHashRef.current = computedHash;

      setEditableData({
        components: compositeMetadata?.components || {
          song: '',
          star: '',
          look: '',
          move: '',
          world: '',
        },
        totalComponents: compositeMetadata?.totalComponents || 0,
        primaryLayer: compositeMetadata?.primaryLayer || '',
        synergy: compositeMetadata?.synergy || '',
        complexity: compositeMetadata?.complexity || '',
        productionValue: compositeMetadata?.productionValue || '',
        targetAudience: compositeMetadata?.targetAudience || '',
        assetType: compositeMetadata?.assetType || 'base',
        baseCompositeId: compositeMetadata?.baseCompositeId || '',
        variantName: compositeMetadata?.variantName || '',
        variantType: compositeMetadata?.variantType || '',
      });
    }
  }, [computedHash, compositeMetadata]);

  // Debounce timeouts for field changes
  const debounceTimeouts = useRef<{ [key: string]: NodeJS.Timeout }>({});

  // Enhanced field change handler with debouncing (following LooksInformation pattern)
  const handleFieldChange = useCallback(
    (field: string, value: string | number | object) => {
      if (field.startsWith('components.')) {
        const componentKey = field.split(
          '.'
        )[1] as keyof typeof editableData.components;
        setEditableData(prev => ({
          ...prev,
          components: {
            ...prev.components,
            [componentKey]: value,
          },
        }));
      } else {
        setEditableData(prev => ({ ...prev, [field]: value }));
      }

      if (debounceTimeouts.current[field]) {
        clearTimeout(debounceTimeouts.current[field]);
      }

      debounceTimeouts.current[field] = setTimeout(() => {
        if (onMetadataChange) {
          setEditableData(current => {
            // Auto-calculate totalComponents based on non-empty components
            const componentCount = Object.values(current.components).filter(
              comp => comp && typeof comp === 'string' && comp.trim() !== ''
            ).length;
            const processedData: CompositesLayerMetadata = {
              layerType: 'composites',
              compositeType: compositeMetadata?.compositeType || 'unknown',
              baseAssetId: compositeMetadata?.baseAssetId || '',
              compositeName:
                compositeMetadata?.compositeName || 'Unnamed Composite',
              baseLookId: compositeMetadata?.baseLookId || '',
              brandInfo: compositeMetadata?.brandInfo || {},
              styleClassification: compositeMetadata?.styleClassification || {},
              visualCharacteristics:
                compositeMetadata?.visualCharacteristics || {},
              contextUsage: compositeMetadata?.contextUsage || {},
              physicalProperties: compositeMetadata?.physicalProperties || {},
              accessories: compositeMetadata?.accessories || {},
              energyLevel: compositeMetadata?.energyLevel || 'medium',
              quality: compositeMetadata?.quality || 'good',
              ...current,
              totalComponents: componentCount,
            };

            onMetadataChange(processedData);
            return current;
          });
        }
      }, 500);
    },
    [onMetadataChange]
  );

  // Cleanup timeouts on unmount
  React.useEffect(() => {
    return () => {
      Object.values(debounceTimeouts.current).forEach(timeout => {
        clearTimeout(timeout);
      });
    };
  }, []);

  // Memoized dropdown options to prevent unnecessary re-renders
  const primaryLayerOptions = useMemo(
    () => ['songs', 'stars', 'looks', 'moves', 'worlds'],
    []
  );

  const synergyOptions = useMemo(
    () => ['Low', 'Medium', 'High', 'Very High', 'Perfect'],
    []
  );

  const complexityOptions = useMemo(
    () => ['Simple', 'Moderate', 'Complex', 'Advanced', 'Expert'],
    []
  );

  const productionValueOptions = useMemo(
    () => [
      'Amateur',
      'Semi-Professional',
      'Professional',
      'Premium',
      'Cinematic',
    ],
    []
  );

  const targetAudienceOptions = useMemo(
    () => [
      'Children',
      'Teen',
      'Young Adult',
      'Adult',
      'General',
      'Professional',
      'Niche',
    ],
    []
  );

  const assetTypeOptions = useMemo(() => ['base', 'variant'], []);

  const variantTypeOptions = useMemo(
    () => [
      'alternate',
      'enhanced',
      'remix',
      'cover',
      'tribute',
      'parody',
      'mashup',
    ],
    []
  );

  // Don't render if no metadata available
  if (
    !compositeMetadata &&
    !asset.compositeMetadata &&
    !asset.aiMetadata?.compositeMetadata
  ) {
    return null;
  }

  return (
    <Paper elevation={2} sx={{ p: 2, mb: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <LayersIcon sx={{ mr: 1, color: 'primary.main' }} />
        <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
          Composite Metadata
        </Typography>
      </Box>

      <Grid container spacing={2}>
        {/* Component References */}
        <Grid item xs={12}>
          <Typography
            variant="subtitle1"
            sx={{ fontWeight: 'bold', mb: 1, color: 'primary.main' }}
          >
            Component References
          </Typography>
        </Grid>

        <Grid item xs={12} md={6}>
          {isEditable ? (
            <TextField
              fullWidth
              label="Song Component (G Layer)"
              value={editableData.components.song}
              onChange={e =>
                handleFieldChange('components.song', e.target.value)
              }
              size="small"
              placeholder="e.g., G.POP.DAN.001"
            />
          ) : (
            <Typography variant="body2">
              <strong>Song Component:</strong>{' '}
              {editableData.components.song || 'Not specified'}
            </Typography>
          )}
        </Grid>

        <Grid item xs={12} md={6}>
          {isEditable ? (
            <TextField
              fullWidth
              label="Star Component (S Layer)"
              value={editableData.components.star}
              onChange={e =>
                handleFieldChange('components.star', e.target.value)
              }
              size="small"
              placeholder="e.g., S.POP.FEM.001"
            />
          ) : (
            <Typography variant="body2">
              <strong>Star Component:</strong>{' '}
              {editableData.components.star || 'Not specified'}
            </Typography>
          )}
        </Grid>

        <Grid item xs={12} md={4}>
          {isEditable ? (
            <TextField
              fullWidth
              label="Look Component (L Layer)"
              value={editableData.components.look}
              onChange={e =>
                handleFieldChange('components.look', e.target.value)
              }
              size="small"
              placeholder="e.g., L.FOR.ELE.001"
            />
          ) : (
            <Typography variant="body2">
              <strong>Look Component:</strong>{' '}
              {editableData.components.look || 'Not specified'}
            </Typography>
          )}
        </Grid>

        <Grid item xs={12} md={4}>
          {isEditable ? (
            <TextField
              fullWidth
              label="Moves Component (M Layer)"
              value={editableData.components.move}
              onChange={e =>
                handleFieldChange('components.move', e.target.value)
              }
              size="small"
              placeholder="e.g., M.CON.LYR.001"
            />
          ) : (
            <Typography variant="body2">
              <strong>Moves Component:</strong>{' '}
              {editableData.components.move || 'Not specified'}
            </Typography>
          )}
        </Grid>

        <Grid item xs={12} md={4}>
          {isEditable ? (
            <TextField
              fullWidth
              label="World Component (W Layer)"
              value={editableData.components.world}
              onChange={e =>
                handleFieldChange('components.world', e.target.value)
              }
              size="small"
              placeholder="e.g., W.STU.DAN.001"
            />
          ) : (
            <Typography variant="body2">
              <strong>World Component:</strong>{' '}
              {editableData.components.world || 'Not specified'}
            </Typography>
          )}
        </Grid>

        {/* Component Details - Aggregated Metadata Display (Read-only for now) */}
        {asset?.components && asset.components.length > 0 && (
          <>
            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Typography
                variant="subtitle1"
                sx={{ fontWeight: 'bold', mb: 2, color: 'primary.main' }}
              >
                📊 Component Details ({asset.components.length} component{asset.components.length !== 1 ? 's' : ''})
              </Typography>
            </Grid>

            {asset.components.map((component: any, index: number) => {
              const layerName = ({
                'G': '🎵 Song',
                'S': '⭐ Star',
                'L': '👗 Look',
                'M': '💃 Move',
                'W': '🌍 World',
                'B': '👑 Brand',
                'P': '🔒 Props'
              } as Record<string, string>)[component.layer] || component.layer;

              return (
                <Grid item xs={12} key={component.id || component._id || index}>
                  <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.50' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, gap: 1 }}>
                      <Chip
                        label={layerName}
                        color="primary"
                        size="small"
                        sx={{ fontWeight: 'bold' }}
                      />
                      <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                        {component.friendlyName || component.name}
                      </Typography>
                    </Box>

                    <Grid container spacing={1}>
                      {/* Layer-specific metadata fields */}
                      {component.layer === 'S' && component.starMetadata && (
                        <>
                          {component.starMetadata.starName && (
                            <Grid item xs={12} sm={6} md={4}>
                              <Typography variant="caption" color="text.secondary">Star Name:</Typography>
                              <Typography variant="body2">{component.starMetadata.starName}</Typography>
                            </Grid>
                          )}
                          {component.starMetadata.gender && (
                            <Grid item xs={12} sm={6} md={4}>
                              <Typography variant="caption" color="text.secondary">Gender:</Typography>
                              <Typography variant="body2">{component.starMetadata.gender}</Typography>
                            </Grid>
                          )}
                          {component.starMetadata.ageGroup && (
                            <Grid item xs={12} sm={6} md={4}>
                              <Typography variant="caption" color="text.secondary">Age Group:</Typography>
                              <Typography variant="body2">{component.starMetadata.ageGroup}</Typography>
                            </Grid>
                          )}
                          {component.starMetadata.archetype && (
                            <Grid item xs={12} sm={6} md={4}>
                              <Typography variant="caption" color="text.secondary">Archetype:</Typography>
                              <Typography variant="body2">{component.starMetadata.archetype}</Typography>
                            </Grid>
                          )}
                          {component.starMetadata.hairColor && (
                            <Grid item xs={12} sm={6} md={4}>
                              <Typography variant="caption" color="text.secondary">Hair Color:</Typography>
                              <Typography variant="body2">{component.starMetadata.hairColor}</Typography>
                            </Grid>
                          )}
                          {component.starMetadata.hairStyle && (
                            <Grid item xs={12} sm={6} md={4}>
                              <Typography variant="caption" color="text.secondary">Hair Style:</Typography>
                              <Typography variant="body2">{component.starMetadata.hairStyle}</Typography>
                            </Grid>
                          )}
                          {component.starMetadata.skinTone && (
                            <Grid item xs={12} sm={6} md={4}>
                              <Typography variant="caption" color="text.secondary">Skin Tone:</Typography>
                              <Typography variant="body2">{component.starMetadata.skinTone}</Typography>
                            </Grid>
                          )}
                          {component.starMetadata.energyLevel && (
                            <Grid item xs={12} sm={6} md={4}>
                              <Typography variant="caption" color="text.secondary">Energy:</Typography>
                              <Typography variant="body2">{component.starMetadata.energyLevel}</Typography>
                            </Grid>
                          )}
                        </>
                      )}

                      {component.layer === 'L' && component.looksMetadata && (
                        <>
                          {component.looksMetadata.looksType && (
                            <Grid item xs={12} sm={6} md={4}>
                              <Typography variant="caption" color="text.secondary">Looks Type:</Typography>
                              <Typography variant="body2">{component.looksMetadata.looksType}</Typography>
                            </Grid>
                          )}
                          {component.looksMetadata.fashionStyle && (
                            <Grid item xs={12} sm={6} md={4}>
                              <Typography variant="caption" color="text.secondary">Fashion Style:</Typography>
                              <Typography variant="body2">{component.looksMetadata.fashionStyle}</Typography>
                            </Grid>
                          )}
                          {component.looksMetadata.formality && (
                            <Grid item xs={12} sm={6} md={4}>
                              <Typography variant="caption" color="text.secondary">Formality:</Typography>
                              <Typography variant="body2">{component.looksMetadata.formality}</Typography>
                            </Grid>
                          )}
                          {component.looksMetadata.primaryColors && component.looksMetadata.primaryColors.length > 0 && (
                            <Grid item xs={12} sm={6} md={4}>
                              <Typography variant="caption" color="text.secondary">Colors:</Typography>
                              <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.5 }}>
                                {component.looksMetadata.primaryColors.slice(0, 3).map((color: string, i: number) => (
                                  <Chip key={i} label={color} size="small" variant="outlined" />
                                ))}
                              </Box>
                            </Grid>
                          )}
                          {component.looksMetadata.materials && component.looksMetadata.materials.length > 0 && (
                            <Grid item xs={12} sm={6} md={4}>
                              <Typography variant="caption" color="text.secondary">Materials:</Typography>
                              <Typography variant="body2">{component.looksMetadata.materials.join(', ')}</Typography>
                            </Grid>
                          )}
                        </>
                      )}

                      {component.layer === 'G' && component.songMetadata && (
                        <>
                          {component.songMetadata.songName && (
                            <Grid item xs={12} sm={6} md={4}>
                              <Typography variant="caption" color="text.secondary">Song Name:</Typography>
                              <Typography variant="body2">{component.songMetadata.songName}</Typography>
                            </Grid>
                          )}
                          {component.songMetadata.genre && component.songMetadata.genre.length > 0 && (
                            <Grid item xs={12} sm={6} md={4}>
                              <Typography variant="caption" color="text.secondary">Genre:</Typography>
                              <Typography variant="body2">{component.songMetadata.genre.join(', ')}</Typography>
                            </Grid>
                          )}
                          {component.songMetadata.tempo && (
                            <Grid item xs={12} sm={6} md={4}>
                              <Typography variant="caption" color="text.secondary">Tempo:</Typography>
                              <Typography variant="body2">{component.songMetadata.tempo} BPM</Typography>
                            </Grid>
                          )}
                          {component.songMetadata.mood && component.songMetadata.mood.length > 0 && (
                            <Grid item xs={12} sm={6} md={4}>
                              <Typography variant="caption" color="text.secondary">Mood:</Typography>
                              <Typography variant="body2">{component.songMetadata.mood.join(', ')}</Typography>
                            </Grid>
                          )}
                          {component.songMetadata.energy && (
                            <Grid item xs={12} sm={6} md={4}>
                              <Typography variant="caption" color="text.secondary">Energy:</Typography>
                              <Typography variant="body2">{component.songMetadata.energy}</Typography>
                            </Grid>
                          )}
                        </>
                      )}

                      {component.layer === 'M' && component.movesMetadata && (
                        <>
                          {component.movesMetadata.movesType && (
                            <Grid item xs={12} sm={6} md={4}>
                              <Typography variant="caption" color="text.secondary">Moves Type:</Typography>
                              <Typography variant="body2">{component.movesMetadata.movesType}</Typography>
                            </Grid>
                          )}
                          {component.movesMetadata.danceStyle && (
                            <Grid item xs={12} sm={6} md={4}>
                              <Typography variant="caption" color="text.secondary">Dance Style:</Typography>
                              <Typography variant="body2">{component.movesMetadata.danceStyle}</Typography>
                            </Grid>
                          )}
                          {component.movesMetadata.intensity && (
                            <Grid item xs={12} sm={6} md={4}>
                              <Typography variant="caption" color="text.secondary">Intensity:</Typography>
                              <Typography variant="body2">{component.movesMetadata.intensity}</Typography>
                            </Grid>
                          )}
                        </>
                      )}

                      {component.layer === 'W' && component.worldsMetadata && (
                        <>
                          {component.worldsMetadata.worldsType && (
                            <Grid item xs={12} sm={6} md={4}>
                              <Typography variant="caption" color="text.secondary">World Type:</Typography>
                              <Typography variant="body2">{component.worldsMetadata.worldsType}</Typography>
                            </Grid>
                          )}
                          {component.worldsMetadata.setting && (
                            <Grid item xs={12} sm={6} md={4}>
                              <Typography variant="caption" color="text.secondary">Setting:</Typography>
                              <Typography variant="body2">{component.worldsMetadata.setting}</Typography>
                            </Grid>
                          )}
                          {component.worldsMetadata.atmosphere && (
                            <Grid item xs={12} sm={6} md={4}>
                              <Typography variant="caption" color="text.secondary">Atmosphere:</Typography>
                              <Typography variant="body2">{component.worldsMetadata.atmosphere}</Typography>
                            </Grid>
                          )}
                        </>
                      )}

                      {/* Fallback for components without layer-specific metadata */}
                      {!component.starMetadata && !component.looksMetadata && !component.songMetadata && !component.movesMetadata && !component.worldsMetadata && (
                        <Grid item xs={12}>
                          <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                            No detailed metadata available for this component
                          </Typography>
                        </Grid>
                      )}
                    </Grid>

                    {/* Component description if available */}
                    {component.description && (
                      <Box sx={{ mt: 1.5, pt: 1.5, borderTop: '1px dashed rgba(0,0,0,0.1)' }}>
                        <Typography variant="caption" color="text.secondary">Description:</Typography>
                        <Typography variant="body2" sx={{ mt: 0.5 }}>
                          {component.description}
                        </Typography>
                      </Box>
                    )}
                  </Paper>
                </Grid>
              );
            })}
          </>
        )}

        <Divider sx={{ width: '100%', my: 2 }} />

        {/* Composition Analysis */}
        <Grid item xs={12}>
          <Typography
            variant="subtitle1"
            sx={{ fontWeight: 'bold', mb: 1, color: 'primary.main' }}
          >
            Composition Analysis
          </Typography>
        </Grid>

        <Grid item xs={12} md={3}>
          {isEditable ? (
            <TextField
              fullWidth
              label="Total Components"
              type="number"
              value={editableData.totalComponents}
              onChange={e =>
                handleFieldChange(
                  'totalComponents',
                  parseInt(e.target.value) || 0
                )
              }
              size="small"
              disabled // Auto-calculated based on component count
            />
          ) : (
            <Typography variant="body2">
              <strong>Total Components:</strong>{' '}
              {editableData.totalComponents || 0}
            </Typography>
          )}
        </Grid>

        <Grid item xs={12} md={3}>
          {isEditable ? (
            <Autocomplete
              fullWidth
              freeSolo
              options={primaryLayerOptions}
              value={editableData.primaryLayer}
              inputValue={editableData.primaryLayer}
              onInputChange={(event, newValue) =>
                handleFieldChange('primaryLayer', newValue || '')
              }
              renderInput={params => (
                <TextField {...params} label="Primary Layer" size="small" />
              )}
            />
          ) : (
            <Typography variant="body2">
              <strong>Primary Layer:</strong>{' '}
              {editableData.primaryLayer || 'Not specified'}
            </Typography>
          )}
        </Grid>

        <Grid item xs={12} md={3}>
          {isEditable ? (
            <Autocomplete
              fullWidth
              freeSolo
              options={synergyOptions}
              value={editableData.synergy}
              inputValue={editableData.synergy}
              onInputChange={(event, newValue) =>
                handleFieldChange('synergy', newValue || '')
              }
              renderInput={params => (
                <TextField {...params} label="Synergy" size="small" />
              )}
            />
          ) : (
            <Typography variant="body2">
              <strong>Synergy:</strong>{' '}
              {editableData.synergy || 'Not specified'}
            </Typography>
          )}
        </Grid>

        <Grid item xs={12} md={3}>
          {isEditable ? (
            <Autocomplete
              fullWidth
              freeSolo
              options={complexityOptions}
              value={editableData.complexity}
              inputValue={editableData.complexity}
              onInputChange={(event, newValue) =>
                handleFieldChange('complexity', newValue || '')
              }
              renderInput={params => (
                <TextField {...params} label="Complexity" size="small" />
              )}
            />
          ) : (
            <Typography variant="body2">
              <strong>Complexity:</strong>{' '}
              {editableData.complexity || 'Not specified'}
            </Typography>
          )}
        </Grid>

        <Divider sx={{ width: '100%', my: 2 }} />

        {/* Production & Audience */}
        <Grid item xs={12}>
          <Typography
            variant="subtitle1"
            sx={{ fontWeight: 'bold', mb: 1, color: 'primary.main' }}
          >
            Production & Audience
          </Typography>
        </Grid>

        <Grid item xs={12} md={6}>
          {isEditable ? (
            <Autocomplete
              fullWidth
              freeSolo
              options={productionValueOptions}
              value={editableData.productionValue}
              inputValue={editableData.productionValue}
              onInputChange={(event, newValue) =>
                handleFieldChange('productionValue', newValue || '')
              }
              renderInput={params => (
                <TextField {...params} label="Production Value" size="small" />
              )}
            />
          ) : (
            <Typography variant="body2">
              <strong>Production Value:</strong>{' '}
              {editableData.productionValue || 'Not specified'}
            </Typography>
          )}
        </Grid>

        <Grid item xs={12} md={6}>
          {isEditable ? (
            <Autocomplete
              fullWidth
              freeSolo
              options={targetAudienceOptions}
              value={editableData.targetAudience}
              inputValue={editableData.targetAudience}
              onInputChange={(event, newValue) =>
                handleFieldChange('targetAudience', newValue || '')
              }
              renderInput={params => (
                <TextField {...params} label="Target Audience" size="small" />
              )}
            />
          ) : (
            <Typography variant="body2">
              <strong>Target Audience:</strong>{' '}
              {editableData.targetAudience || 'Not specified'}
            </Typography>
          )}
        </Grid>

        <Divider sx={{ width: '100%', my: 2 }} />

        {/* Asset Type & Variants */}
        <Grid item xs={12}>
          <Typography
            variant="subtitle1"
            sx={{ fontWeight: 'bold', mb: 1, color: 'primary.main' }}
          >
            Asset Type & Variants
          </Typography>
        </Grid>

        <Grid item xs={12} md={4}>
          {isEditable ? (
            <Autocomplete
              fullWidth
              freeSolo
              options={assetTypeOptions}
              value={editableData.assetType}
              inputValue={editableData.assetType}
              onInputChange={(event, newValue) =>
                handleFieldChange('assetType', newValue || '')
              }
              renderInput={params => (
                <TextField {...params} label="Asset Type" size="small" />
              )}
            />
          ) : (
            <Typography variant="body2">
              <strong>Asset Type:</strong>{' '}
              {editableData.assetType || 'Not specified'}
            </Typography>
          )}
        </Grid>

        {editableData.assetType === 'variant' && (
          <>
            <Grid item xs={12} md={4}>
              {isEditable ? (
                <TextField
                  fullWidth
                  label="Base Composite ID"
                  value={editableData.baseCompositeId}
                  onChange={e =>
                    handleFieldChange('baseCompositeId', e.target.value)
                  }
                  size="small"
                  placeholder="e.g., C.FUL.POP.001"
                />
              ) : (
                <Typography variant="body2">
                  <strong>Base Composite ID:</strong>{' '}
                  {editableData.baseCompositeId || 'Not specified'}
                </Typography>
              )}
            </Grid>

            <Grid item xs={12} md={4}>
              {isEditable ? (
                <TextField
                  fullWidth
                  label="Variant Name"
                  value={editableData.variantName}
                  onChange={e =>
                    handleFieldChange('variantName', e.target.value)
                  }
                  size="small"
                  placeholder="e.g., Extended Version"
                />
              ) : (
                <Typography variant="body2">
                  <strong>Variant Name:</strong>{' '}
                  {editableData.variantName || 'Not specified'}
                </Typography>
              )}
            </Grid>

            <Grid item xs={12} md={12}>
              {isEditable ? (
                <Autocomplete
                  fullWidth
                  freeSolo
                  options={variantTypeOptions}
                  value={editableData.variantType}
                  inputValue={editableData.variantType}
                  onInputChange={(event, newValue) =>
                    handleFieldChange('variantType', newValue || '')
                  }
                  renderInput={params => (
                    <TextField {...params} label="Variant Type" size="small" />
                  )}
                />
              ) : (
                <Typography variant="body2">
                  <strong>Variant Type:</strong>{' '}
                  {editableData.variantType || 'Not specified'}
                </Typography>
              )}
            </Grid>
          </>
        )}

        {/* Component Summary Display */}
        {Object.values(editableData.components).some(
          comp => comp && typeof comp === 'string' && comp.trim() !== ''
        ) && (
          <>
            <Divider sx={{ width: '100%', my: 2 }} />
            <Grid item xs={12}>
              <Typography
                variant="subtitle1"
                sx={{ fontWeight: 'bold', mb: 1, color: 'primary.main' }}
              >
                Component Summary
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {Object.entries(editableData.components).map(
                  ([layer, componentId]) => {
                    if (
                      !componentId ||
                      typeof componentId !== 'string' ||
                      componentId.trim() === ''
                    )
                      return null;

                    const layerColors = {
                      song: 'success',
                      star: 'warning',
                      look: 'info',
                      moves: 'secondary',
                      world: 'primary',
                    } as const;

                    return (
                      <Chip
                        key={layer}
                        label={`${layer.toUpperCase()}: ${componentId}`}
                        color={
                          layerColors[layer as keyof typeof layerColors] ||
                          'default'
                        }
                        variant="outlined"
                        size="small"
                      />
                    );
                  }
                )}
              </Box>
            </Grid>
          </>
        )}

        <Divider sx={{ width: '100%', my: 3 }} />

        {/* ========== COMPOSITE VALIDATION SECTION ========== */}
        <Grid item xs={12}>
          <Box sx={{
            p: 3,
            backgroundColor: validationResult.isValid ? 'success.light' : 'error.light',
            borderRadius: 2,
            border: '2px solid',
            borderColor: validationResult.isValid ? 'success.main' : 'error.main'
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              {validationResult.isValid ? (
                <CheckCircleIcon sx={{ mr: 1, color: 'success.dark' }} />
              ) : (
                <ErrorIcon sx={{ mr: 1, color: 'error.dark' }} />
              )}
              <Typography variant="h6" sx={{ fontWeight: 'bold', color: validationResult.isValid ? 'success.dark' : 'error.dark' }}>
                Composite Validation
              </Typography>
            </Box>

            {/* Completeness Indicator */}
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
                Completeness: {validationResult.completenessPercentage}%
              </Typography>
              <Box sx={{
                width: '100%',
                height: 8,
                backgroundColor: 'rgba(0,0,0,0.1)',
                borderRadius: 1,
                overflow: 'hidden'
              }}>
                <Box sx={{
                  width: `${validationResult.completenessPercentage}%`,
                  height: '100%',
                  backgroundColor: validationResult.completenessPercentage === 100 ? 'primary.main' : 'warning.main',
                  transition: 'width 0.3s ease'
                }} />
              </Box>
            </Box>

            {/* Component Status Grid */}
            <Grid container spacing={1} sx={{ mb: 2 }}>
              {Object.entries(validationResult.componentStatus).map(([layer, status]) => {
                const layerLabels: Record<string, string> = {
                  songs: 'Songs (G)',
                  stars: 'Stars (S)',
                  looks: 'Looks (L)',
                  moves: 'Moves (M)',
                  worlds: 'Worlds (W)',
                };

                return (
                  <Grid item xs={12} sm={6} md={4} key={layer}>
                    <Box sx={{
                      p: 1.5,
                      backgroundColor: 'background.paper',
                      borderRadius: 1,
                      border: '1px solid',
                      borderColor: status.exists ? 'success.main' : 'error.main'
                    }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                        {status.exists ? (
                          <CheckCircleIcon sx={{ fontSize: 16, mr: 0.5, color: 'success.main' }} />
                        ) : (
                          <ErrorIcon sx={{ fontSize: 16, mr: 0.5, color: 'error.main' }} />
                        )}
                        <Typography variant="caption" sx={{ fontWeight: 'bold' }}>
                          {layerLabels[layer]}
                        </Typography>
                      </Box>
                      <Typography variant="caption" color="text.secondary">
                        {status.exists ? (
                          status.hasAlgoRhythmMetadata ? (
                            '✅ Has AlgoRhythm metadata'
                          ) : (
                            '⚠️ Missing AlgoRhythm metadata'
                          )
                        ) : (
                          '❌ Component missing'
                        )}
                      </Typography>
                    </Box>
                  </Grid>
                );
              })}
            </Grid>

            {/* Validation Errors */}
            {validationResult.errors.length > 0 && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1, color: 'error.dark' }}>
                  ❌ Errors ({validationResult.errors.length}):
                </Typography>
                {validationResult.errors.map((error, index) => (
                  <Box key={index} sx={{ pl: 2, mb: 0.5 }}>
                    <Typography variant="caption" color="error.dark">
                      • {error.message}
                    </Typography>
                  </Box>
                ))}
              </Box>
            )}

            {/* Validation Warnings */}
            {validationResult.warnings.length > 0 && (
              <Box sx={{
                p: 2,
                backgroundColor: 'rgba(237, 108, 2, 0.08)',
                borderRadius: 1,
                border: '1px solid',
                borderColor: 'rgba(237, 108, 2, 0.2)'
              }}>
                <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1, color: 'warning.dark' }}>
                  ⚠️ Warnings ({validationResult.warnings.length}):
                </Typography>
                {validationResult.warnings.map((warning, index) => (
                  <Box key={index} sx={{ pl: 2, mb: 0.5 }}>
                    <Typography variant="caption" sx={{ color: 'text.primary' }}>
                      • {warning.message}
                    </Typography>
                  </Box>
                ))}
              </Box>
            )}

            {/* Success Message */}
            {validationResult.isValid && validationResult.warnings.length === 0 && (
              <Box sx={{ p: 2, backgroundColor: 'rgba(25, 118, 210, 0.08)', borderRadius: 1, border: '1px solid rgba(25, 118, 210, 0.2)' }}>
                <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                  ✅ Composite is complete and valid! All 5 components are present and have required AlgoRhythm metadata.
                </Typography>
              </Box>
            )}
          </Box>
        </Grid>
      </Grid>
    </Paper>
  );
};

export default CompositeMetadataEditor;
